import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  Braces,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock,
  FileCode,
  Link2,
  Loader2,
  Mail,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Send,
  SendHorizonal,
  Sparkles,
  Trash2,
  Users,
  Workflow,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { supabase } from '../../lib/supabase';
import { tierLabel } from '../../lib/personalization';
import ConfirmModal from '../Common/ConfirmModal';
import EnrollSubscriberModal from '../Automations/EnrollSubscriberModal';
import SendScheduledEmailNowModal from './SendScheduledEmailNowModal';
import SubscriberEngagementSection from './SubscriberEngagementSection';
import SubscriberPostmarkSentSection from './SubscriberPostmarkSentSection';
import SubscriberSiteActivitySection from './SubscriberSiteActivitySection';
import SubscriberLinkedTranscriptsSection from './SubscriberLinkedTranscriptsSection';
import RecordNotesPanel from '../RecordNotes/RecordNotesPanel';
import SubscriberBookingsSection from './SubscriberBookingsSection';
import SubscriberReferralsSection from './SubscriberReferralsSection';
import SubscriberFunnelFieldsSection from './SubscriberFunnelFieldsSection';
import type {
  AutomationEnrollmentRow,
  AutomationEnrollmentStatus,
  AutomationRow,
} from '../Automations/types';
import PersonalizationStatusPill from './PersonalizationStatusPill';
import ScheduleEmailModal from './ScheduleEmailModal';
import RecommendContentModal from './RecommendContentModal';
import TestContentLinkModal from './TestContentLinkModal';
import SubscriberJsonModal from './SubscriberJsonModal';
import SubscriberModal from './SubscriberModal';
import SendSubscriberInviteModal from './SendSubscriberInviteModal';
import ConfirmGmailDraftModal from './ConfirmGmailDraftModal';
import EmailSummaryModal from './EmailSummaryModal';
import SubscriberNetworkingTasksSection from './SubscriberNetworkingTasksSection';
import { canSendNewsletterInvite, mergeNewsletterInviteSendHistory, loadNewsletterInvitePostmarkMessages } from '../../lib/subscriberNewsletterInvite';
import { loadPostmarkOutboundMessages, type PostmarkOutboundMessage } from '../../lib/postmarkOutboundMessages';
import { subscriberInviteGreetingName } from '../../lib/subscriberDisplay';
import { openConnectionFollowUpInGmail } from '../../lib/gmailComposeUrl';
import {
  hasReverseContactResult,
  lookupSubscriberWithReverseContact,
} from '../../lib/reverseContactLookup';
import SubscriberCustomFieldsForm from './SubscriberCustomFieldsForm';
import {
  mapDefinitionRow,
  readCustomFieldUrl,
  type CustomDataMap,
  type SubscriberFieldDefinitionRow,
} from '../../lib/subscriberCustomFields';
import { channelLabel, channelsToKeyArray, fetchActiveChannels, visibilityLabel, type ChannelRow, type ChannelVisibility } from '../../lib/channels';
import { SUBSCRIBER_EMAIL_STATUSES, subscriberEmailStatus } from '../../lib/subscriberEmailStatus';
import {
  SUBSCRIBER_ACCESS_STATUSES,
  loadMcpAccessStatus,
  saveMcpAccessStatus,
} from '../../lib/subscriberAccessStatus';
import { subscriberRoleLabel } from '../../lib/subscriberRoles';
import { subscriberSource, subscriberSourceLabel } from '../../lib/subscriberSource';
import type { ScheduledEmailRow, ScheduledEmailStatus, SubscriberRow } from './types';

interface EnrollmentWithAutomation extends AutomationEnrollmentRow {
  automation: Pick<AutomationRow, 'id' | 'name' | 'status' | 'tz'> | null;
}

function bestEmail(row: SubscriberRow | null): string {
  if (!row) return '';
  const e = typeof row.email === 'string' ? row.email.trim() : '';
  if (e) return e;
  const alt = row.email_address ?? row.emailAddress;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

function bestName(row: SubscriberRow | null): string {
  if (!row) return '';
  const n = typeof row.name === 'string' ? row.name.trim() : '';
  if (n) return n;
  const alt = row.full_name ?? row.fullName;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

function bestRole(row: SubscriberRow | null): string {
  if (!row) return '';
  return typeof row.role === 'string' ? row.role.trim() : '';
}

function SubscriberHeaderImage({ customData }: { customData: CustomDataMap | null | undefined }) {
  const photoUrl = readCustomFieldUrl(customData?.photo_url);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  if (!photoUrl || failedUrl === photoUrl) {
    return <Users className="w-7 h-7 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-emerald-500 shrink-0" />;
  }

  return (
    <img
      src={photoUrl}
      alt=""
      width={32}
      height={32}
      className="w-7 h-7 sm:w-8 sm:h-8 mr-2 sm:mr-3 rounded-md object-cover shrink-0 bg-emerald-500"
      referrerPolicy="no-referrer"
      onError={() => setFailedUrl(photoUrl)}
    />
  );
}

const RELATIVE_UNITS: ReadonlyArray<{ limit: number; div: number; one: string; many: string }> = [
  { limit: 60, div: 1, one: 'second', many: 'seconds' },
  { limit: 60 * 60, div: 60, one: 'min', many: 'min' },
  { limit: 60 * 60 * 24, div: 60 * 60, one: 'hour', many: 'hours' },
  { limit: 60 * 60 * 24 * 30, div: 60 * 60 * 24, one: 'day', many: 'days' },
  { limit: 60 * 60 * 24 * 365, div: 60 * 60 * 24 * 30, one: 'month', many: 'months' },
  { limit: Infinity, div: 60 * 60 * 24 * 365, one: 'year', many: 'years' },
];

/**
 * Past-tense relative time like "5 min ago" / "2 hours ago". Returns null
 * if `targetIso` can't be parsed. Returns "just now" when within ~30s.
 */
function formatTimeAgo(targetIso: unknown, nowMs: number): string | null {
  if (typeof targetIso !== 'string' || !targetIso) return null;
  const targetMs = new Date(targetIso).getTime();
  if (Number.isNaN(targetMs)) return null;
  const diffSec = Math.round((nowMs - targetMs) / 1000);
  if (diffSec < 30) return 'just now';
  for (const u of RELATIVE_UNITS) {
    if (diffSec < u.limit) {
      const n = Math.max(1, Math.round(diffSec / u.div));
      return `${n} ${n === 1 ? u.one : u.many} ago`;
    }
  }
  return 'a while ago';
}

/** Short unsubscribe label for contact details, e.g. "Jun 19 · 3 days ago". */
function formatUnsubscribeAgoLabel(unsubscribedAt: unknown, nowMs = Date.now()): string | null {
  if (typeof unsubscribedAt !== 'string' || !unsubscribedAt) return null;
  const ago = formatTimeAgo(unsubscribedAt, nowMs);
  if (!ago) return null;
  const d = new Date(unsubscribedAt);
  if (Number.isNaN(d.getTime())) return ago;
  const short = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${short} · ${ago}`;
}

/**
 * Format a relative time delta from `targetMs` against `nowMs` like
 * "(sending in 5 min)" or "(sending now)". Returns null if the target
 * isn't parseable.
 */
function formatSendingIn(targetIso: unknown, nowMs: number): string | null {
  if (typeof targetIso !== 'string' || !targetIso) return null;
  const targetMs = new Date(targetIso).getTime();
  if (Number.isNaN(targetMs)) return null;

  const diffSec = Math.round((targetMs - nowMs) / 1000);

  if (diffSec <= 30) return 'sending now';

  for (const u of RELATIVE_UNITS) {
    if (diffSec < u.limit) {
      const n = Math.max(1, Math.round(diffSec / u.div));
      return `sending in ${n} ${n === 1 ? u.one : u.many}`;
    }
  }
  return 'sending soon';
}

function formatWhen(value: unknown): string {
  if (value == null) return '—';
  if (typeof value !== 'string' && typeof value !== 'number') return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(value);
  }
}

/** Render a scalar value safely as a string. */
function renderScalar(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v.trim() ? v : '—';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

type DetailTab =
  | 'details'
  | 'tasks'
  | 'transcripts'
  | 'notes'
  | 'scheduled'
  | 'engagement'
  | 'site'
  | 'automations'
  | 'postmark';

const DETAIL_TABS: readonly DetailTab[] = [
  'details',
  'tasks',
  'transcripts',
  'notes',
  'scheduled',
  'engagement',
  'site',
  'automations',
  'postmark',
] as const;
const DEFAULT_TAB: DetailTab = 'details';

function parseTab(raw: string | null): DetailTab {
  return DETAIL_TABS.includes(raw as DetailTab) ? (raw as DetailTab) : DEFAULT_TAB;
}

const TOP_LEVEL_KEYS = new Set([
  'id',
  'email',
  'name',
  'channels',
  'custom_data',
  'email_status',
  'funnel_status',
  'last_contacted_at',
  'created_at',
  'updated_at',
  'email_address',
  'emailAddress',
  'full_name',
  'fullName',
  'profile_score',
  'profile_scored_at',
  'profile_score_breakdown',
  'profile_score_tier',
  'beehiiv_subscription_id',
  'beehiiv_synced_at',
  'beehiiv_sync_error',
  'beehiiv_last_sent_at',
  'unsubscribed_at',
  'source',
  'role',
]);

export default function SubscriberDetailView() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [row, setRow] = useState<SubscriberRow | null>(null);
  const [channelCatalog, setChannelCatalog] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [postmarkInviteMessages, setPostmarkInviteMessages] = useState<PostmarkOutboundMessage[]>([]);
  const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);
  const [confirmGmailDraftOpen, setConfirmGmailDraftOpen] = useState(false);
  /** Default off — keep prior confirm links working unless you opt in. */
  const [rotateConfirmToken, setRotateConfirmToken] = useState(false);
  const [emailSummaryOpen, setEmailSummaryOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [recommendOpen, setRecommendOpen] = useState(false);
  const [testLinkOpen, setTestLinkOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const mobileActionsMenuRef = useRef<HTMLDivElement>(null);
  const [editingScheduled, setEditingScheduled] = useState<ScheduledEmailRow | null>(null);

  // Tab state is sourced from `?tab=...` so hard refreshes and back/forward
  // preserve the active tab.
  const activeTab = parseTab(searchParams.get('tab'));
  const setActiveTab = useCallback(
    (tab: DetailTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === DEFAULT_TAB) next.delete('tab');
          else next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [scheduledEmails, setScheduledEmails] = useState<ScheduledEmailRow[]>([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledError, setScheduledError] = useState<string | null>(null);
  const [scheduledRealtime, setScheduledRealtime] = useState(false);


  const [enrollments, setEnrollments] = useState<EnrollmentWithAutomation[]>([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [enrollmentsError, setEnrollmentsError] = useState<string | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [recomputingProfile, setRecomputingProfile] = useState(false);

  const [fieldDefinitions, setFieldDefinitions] = useState<SubscriberFieldDefinitionRow[]>([]);
  const [fieldDefinitionsLoading, setFieldDefinitionsLoading] = useState(false);

  const [openTasksCount, setOpenTasksCount] = useState(0);

  const [emailInlineEdit, setEmailInlineEdit] = useState(false);
  const [emailInlineDraft, setEmailInlineDraft] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [lookingUp, setLookingUp] = useState(false);
  const [lookupConfirmOpen, setLookupConfirmOpen] = useState(false);
  const [fieldsRefreshKey, setFieldsRefreshKey] = useState(0);

  const [nameInlineEdit, setNameInlineEdit] = useState(false);
  const [nameInlineDraft, setNameInlineDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [sourceInlineEdit, setSourceInlineEdit] = useState(false);
  const [sourceInlineDraft, setSourceInlineDraft] = useState('');
  const [sourceSaving, setSourceSaving] = useState(false);
  const sourceInputRef = useRef<HTMLInputElement>(null);

  const [roleInlineEdit, setRoleInlineEdit] = useState(false);
  const [roleInlineDraft, setRoleInlineDraft] = useState('');
  const [roleSaving, setRoleSaving] = useState(false);
  const roleInputRef = useRef<HTMLInputElement>(null);

  const [emailStatusInlineEdit, setEmailStatusInlineEdit] = useState(false);
  const [emailStatusInlineDraft, setEmailStatusInlineDraft] = useState('');
  const [emailStatusSaving, setEmailStatusSaving] = useState(false);
  const emailStatusSelectRef = useRef<HTMLSelectElement>(null);

  const [accessStatus, setAccessStatus] = useState('not_applied');
  const [accessStatusInlineEdit, setAccessStatusInlineEdit] = useState(false);
  const [accessStatusInlineDraft, setAccessStatusInlineDraft] = useState('');
  const [accessStatusSaving, setAccessStatusSaving] = useState(false);
  const accessStatusSelectRef = useRef<HTMLSelectElement>(null);

  const [channelsInlineEdit, setChannelsInlineEdit] = useState(false);
  const [channelsInlineDraft, setChannelsInlineDraft] = useState<string[]>([]);
  const [channelsSaving, setChannelsSaving] = useState(false);

  const loadFieldDefinitions = useCallback(async () => {
    setFieldDefinitionsLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscriber_field_definitions')
        .select('*')
        .eq('active', true)
        .order('key', { ascending: true });
      if (error) throw error;
      setFieldDefinitions(
        (data ?? []).map((raw) => mapDefinitionRow(raw as Record<string, unknown>)),
      );
    } catch (e: unknown) {
      console.warn('field definitions load failed:', e instanceof Error ? e.message : e);
      setFieldDefinitions([]);
    } finally {
      setFieldDefinitionsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (!idParam) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setNotFound(false);
    try {
      // The `id` column may be a uuid (string) or bigint (number). Compare as string -- PostgREST coerces.
      const asNumber = Number(idParam);
      const idValue: string | number = Number.isFinite(asNumber) && /^\d+$/.test(idParam) ? asNumber : idParam;
      const { data, error } = await supabase
        .from('subscribers')
        .select('*')
        .eq('id', idValue as any)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setNotFound(true);
        setRow(null);
        return;
      }
      setRow(data as SubscriberRow);
      setAccessStatus(await loadMcpAccessStatus(String((data as SubscriberRow).id ?? idValue)));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load subscriber';
      setLoadError(msg);
      reportUiError('subscribers', 'operation', e);
  toast.error(msg);
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [idParam]);

  /** Refetch the row without toggling the page-level loading spinner. */
  const refreshRow = useCallback(async () => {
    if (!idParam) return;
    const asNumber = Number(idParam);
    const idValue: string | number =
      Number.isFinite(asNumber) && /^\d+$/.test(idParam) ? asNumber : idParam;
    const { data, error } = await supabase
      .from('subscribers')
      .select('*')
      .eq('id', idValue as any)
      .maybeSingle();
    if (!error && data) {
      setRow(data as SubscriberRow);
      setAccessStatus(await loadMcpAccessStatus(String((data as SubscriberRow).id ?? idValue)));
    }
  }, [idParam]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inDesktopMenu = actionsMenuRef.current?.contains(target);
      const inMobileMenu = mobileActionsMenuRef.current?.contains(target);
      if (!inDesktopMenu && !inMobileMenu) {
        setActionsMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActionsMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [actionsMenuOpen]);

  useEffect(() => {
    void loadFieldDefinitions();
  }, [loadFieldDefinitions]);

  const subscriberId = row?.id != null ? String(row.id) : '';

  const refreshOpenTasksCount = useCallback(async () => {
    if (!subscriberId) {
      setOpenTasksCount(0);
      return;
    }
    try {
      const { count, error } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('subscriber_id', subscriberId)
        .in('status', ['pending', 'in_progress']);
      if (error) throw error;
      setOpenTasksCount(count ?? 0);
    } catch {
      setOpenTasksCount(0);
    }
  }, [subscriberId]);

  useEffect(() => {
    void refreshOpenTasksCount();
  }, [refreshOpenTasksCount]);

  const handleTasksChanged = useCallback(() => {
    void refreshOpenTasksCount();
  }, [refreshOpenTasksCount]);

  const customData = useMemo((): CustomDataMap | null => {
    const cd = row?.custom_data;
    if (cd && typeof cd === 'object' && !Array.isArray(cd)) {
      return cd as CustomDataMap;
    }
    return null;
  }, [row?.custom_data]);

  const customFieldBackgroundRefreshRef = useRef<number | null>(null);

  const handleCustomFieldSaved = useCallback(
    (key: string, value: unknown | null) => {
      setRow((prev) => {
        if (!prev) return prev;
        const existing =
          prev.custom_data && typeof prev.custom_data === 'object' && !Array.isArray(prev.custom_data)
            ? { ...(prev.custom_data as Record<string, unknown>) }
            : {};
        if (value === null) delete existing[key];
        else existing[key] = value;
        return {
          ...prev,
          custom_data: existing,
          updated_at: new Date().toISOString(),
        };
      });

      if (customFieldBackgroundRefreshRef.current != null) {
        window.clearTimeout(customFieldBackgroundRefreshRef.current);
      }
      customFieldBackgroundRefreshRef.current = window.setTimeout(() => {
        customFieldBackgroundRefreshRef.current = null;
        void refreshRow();
      }, 4000);
    },
    [refreshRow],
  );

  useEffect(
    () => () => {
      if (customFieldBackgroundRefreshRef.current != null) {
        window.clearTimeout(customFieldBackgroundRefreshRef.current);
      }
    },
    [],
  );

  const loadScheduledEmails = useCallback(async () => {
    if (!subscriberId) {
      setScheduledEmails([]);
      return;
    }
    setScheduledLoading(true);
    setScheduledError(null);
    try {
      const { data, error } = await supabase
        .from('scheduled_emails')
        .select('*')
        .eq('subscriber_id', subscriberId)
        .order('scheduled_at', { ascending: true })
        .order('created_at', { ascending: true })
        .limit(200);
      if (error) throw error;
      setScheduledEmails((data ?? []) as ScheduledEmailRow[]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load scheduled emails';
      setScheduledError(msg);
      setScheduledEmails([]);
    } finally {
      setScheduledLoading(false);
    }
  }, [subscriberId]);

  const loadEnrollments = useCallback(async () => {
    if (!subscriberId) {
      setEnrollments([]);
      return;
    }
    setEnrollmentsLoading(true);
    setEnrollmentsError(null);
    try {
      const { data, error } = await supabase
        .from('automation_enrollments')
        .select('*, automation:automations(id, name, status, tz)')
        .eq('subscriber_id', subscriberId)
        .order('enrolled_at', { ascending: false });
      if (error) throw error;
      setEnrollments((data ?? []) as EnrollmentWithAutomation[]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load enrollments';
      setEnrollmentsError(msg);
      setEnrollments([]);
    } finally {
      setEnrollmentsLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void loadEnrollments();
  }, [loadEnrollments]);

  const handleCancelEnrollment = useCallback(
    async (enrollmentId: string) => {
      if (!window.confirm('Cancel this enrollment? Any pending sends will be canceled.')) return;
      try {
        const { error } = await supabase.rpc('cancel_automation_enrollment', {
          p_enrollment_id: enrollmentId,
        });
        if (error) throw error;
        toast.success('Enrollment canceled');
        await Promise.all([loadEnrollments(), loadScheduledEmails()]);
      } catch (e: unknown) {
        reportUiError('subscribers', 'operation', e);
  toast.error(e instanceof Error ? e.message : 'Cancel failed');
      }
    },
    [loadEnrollments, loadScheduledEmails],
  );

  useEffect(() => {
    void loadScheduledEmails();
  }, [loadScheduledEmails]);

  // Live updates for scheduled_emails belonging to this subscriber.
  // Strategy: subscribe to postgres_changes; on any event, refetch the list.
  // (We refetch instead of patching state so we always get fresh server-side
  // ordering, derived counts, and any rows we wouldn't otherwise see.)
  const loadScheduledRef = useRef(loadScheduledEmails);
  useEffect(() => {
    loadScheduledRef.current = loadScheduledEmails;
  }, [loadScheduledEmails]);

  useEffect(() => {
    if (!subscriberId) return;
    setScheduledRealtime(false);
    const channel = supabase
      .channel(`scheduled_emails:subscriber:${subscriberId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scheduled_emails',
          filter: `subscriber_id=eq.${subscriberId}`,
        },
        () => {
          void loadScheduledRef.current();
        },
      )
      .subscribe((status) => {
        setScheduledRealtime(status === 'SUBSCRIBED');
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [subscriberId]);

  // Polling fallback (and a backstop against realtime hiccups). Only runs
  // when the user is looking at the scheduled tab and the tab is visible.
  useEffect(() => {
    if (!subscriberId) return;
    if (activeTab !== 'scheduled') return;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void loadScheduledRef.current();
    };
    const intervalMs = scheduledRealtime ? 60_000 : 15_000;
    const handle = window.setInterval(tick, intervalMs);

    const onVisibility = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(handle);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [subscriberId, activeTab, scheduledRealtime]);

  const email = bestEmail(row);
  const name = bestName(row);
  const role = bestRole(row);
  const roleDisplay = role ? subscriberRoleLabel(role) : '';
  const source = subscriberSource(row);
  const sourceDisplay = source ? subscriberSourceLabel(source) || source : '';

  useLayoutEffect(() => {
    if (emailInlineEdit) {
      emailInputRef.current?.focus();
      emailInputRef.current?.select();
    }
  }, [emailInlineEdit]);

  useLayoutEffect(() => {
    if (nameInlineEdit) nameInputRef.current?.focus();
  }, [nameInlineEdit]);

  useLayoutEffect(() => {
    if (sourceInlineEdit) sourceInputRef.current?.focus();
  }, [sourceInlineEdit]);

  useLayoutEffect(() => {
    if (roleInlineEdit) roleInputRef.current?.focus();
  }, [roleInlineEdit]);

  useLayoutEffect(() => {
    if (emailStatusInlineEdit) emailStatusSelectRef.current?.focus();
  }, [emailStatusInlineEdit]);

  useLayoutEffect(() => {
    if (accessStatusInlineEdit) accessStatusSelectRef.current?.focus();
  }, [accessStatusInlineEdit]);

  useEffect(() => {
    setEmailInlineEdit(false);
    setEmailInlineDraft('');
    setNameInlineEdit(false);
    setNameInlineDraft('');
    setSourceInlineEdit(false);
    setSourceInlineDraft('');
    setRoleInlineEdit(false);
    setRoleInlineDraft('');
    setEmailStatusInlineEdit(false);
    setEmailStatusInlineDraft('');
    setAccessStatusInlineEdit(false);
    setAccessStatusInlineDraft('');
    setChannelsInlineEdit(false);
    setChannelsInlineDraft([]);
  }, [row?.id]);

  const startEmailInlineEdit = useCallback(() => {
    setEmailInlineDraft(email);
    setEmailInlineEdit(true);
  }, [email]);

  const cancelEmailInlineEdit = useCallback(() => {
    setEmailInlineEdit(false);
    setEmailInlineDraft(email);
  }, [email]);

  const commitEmailInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    const next = emailInlineDraft.trim().toLowerCase();
    const prev = email.trim().toLowerCase();
    if (!next || !next.includes('@')) {
      toast.error('Valid email is required');
      return;
    }
    if (next === prev) {
      setEmailInlineEdit(false);
      return;
    }
    setEmailSaving(true);
    try {
      const { error } = await supabase
        .from('subscribers')
        .update({
          email: next,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id as string | number);
      if (error) throw error;
      toast.success('Email updated');
      setEmailInlineEdit(false);
      await refreshRow();
    } catch (e: unknown) {
      reportUiError('subscribers', 'update_email', e, { subscriber_id: String(row.id) });
      const message = e instanceof Error ? e.message : 'Could not save email';
      if (/duplicate|unique|already exists/i.test(message)) {
        toast.error('Another connection already uses that email');
      } else {
        toast.error(message);
      }
    } finally {
      setEmailSaving(false);
    }
  }, [email, emailInlineDraft, refreshRow, row]);

  const startNameInlineEdit = useCallback(() => {
    setNameInlineDraft(name);
    setNameInlineEdit(true);
  }, [name]);

  const cancelNameInlineEdit = useCallback(() => {
    setNameInlineEdit(false);
    setNameInlineDraft(name);
  }, [name]);

  const commitNameInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    const next = nameInlineDraft.trim();
    const prev = name.trim();
    if (next === prev) {
      setNameInlineEdit(false);
      return;
    }
    setNameSaving(true);
    try {
      const { error } = await supabase
        .from('subscribers')
        .update({
          name: next || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id as string | number);
      if (error) throw error;
      toast.success('Name updated');
      setNameInlineEdit(false);
      await refreshRow();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
  toast.error(e instanceof Error ? e.message : 'Could not save name');
    } finally {
      setNameSaving(false);
    }
  }, [name, nameInlineDraft, refreshRow, row]);

  const startSourceInlineEdit = useCallback(() => {
    setSourceInlineDraft(source);
    setSourceInlineEdit(true);
  }, [source]);

  const cancelSourceInlineEdit = useCallback(() => {
    setSourceInlineEdit(false);
    setSourceInlineDraft(source);
  }, [source]);

  const commitSourceInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    const next = sourceInlineDraft.trim();
    const prev = source.trim();
    if (next === prev) {
      setSourceInlineEdit(false);
      return;
    }
    setSourceSaving(true);
    try {
      const { error } = await supabase
        .from('subscribers')
        .update({
          source: next || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id as string | number);
      if (error) throw error;
      toast.success('Source updated');
      setSourceInlineEdit(false);
      await refreshRow();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
  toast.error(e instanceof Error ? e.message : 'Could not save source');
    } finally {
      setSourceSaving(false);
    }
  }, [refreshRow, row, source, sourceInlineDraft]);

  const startRoleInlineEdit = useCallback(() => {
    setRoleInlineDraft(role);
    setRoleInlineEdit(true);
  }, [role]);

  const cancelRoleInlineEdit = useCallback(() => {
    setRoleInlineEdit(false);
    setRoleInlineDraft(role);
  }, [role]);

  const commitRoleInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    const next = roleInlineDraft.trim();
    const prev = role.trim();
    if (next === prev) {
      setRoleInlineEdit(false);
      return;
    }
    setRoleSaving(true);
    try {
      const { error } = await supabase
        .from('subscribers')
        .update({
          role: next || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id as string | number);
      if (error) throw error;
      toast.success('Role updated');
      setRoleInlineEdit(false);
      await refreshRow();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
      toast.error(e instanceof Error ? e.message : 'Could not save role');
    } finally {
      setRoleSaving(false);
    }
  }, [refreshRow, role, roleInlineDraft, row]);

  const emailStatusCurrent = subscriberEmailStatus(row);

  const startEmailStatusInlineEdit = useCallback(() => {
    setEmailStatusInlineDraft(emailStatusCurrent || 'raw');
    setEmailStatusInlineEdit(true);
  }, [emailStatusCurrent]);

  const cancelEmailStatusInlineEdit = useCallback(() => {
    setEmailStatusInlineEdit(false);
    setEmailStatusInlineDraft(emailStatusCurrent || 'raw');
  }, [emailStatusCurrent]);

  const commitEmailStatusInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    const next = emailStatusInlineDraft.trim();
    const prev = emailStatusCurrent.trim();
    if (!next || next === prev) {
      setEmailStatusInlineEdit(false);
      return;
    }
    if (!SUBSCRIBER_EMAIL_STATUSES.some((item) => item.value === next)) {
      toast.error('Invalid email status');
      return;
    }
    setEmailStatusSaving(true);
    try {
      const { error } = await supabase
        .from('subscribers')
        .update({
          email_status: next,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id as string | number);
      if (error) throw error;
      toast.success('Email status updated');
      setEmailStatusInlineEdit(false);
      await refreshRow();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
      toast.error(e instanceof Error ? e.message : 'Could not save email status');
    } finally {
      setEmailStatusSaving(false);
    }
  }, [emailStatusCurrent, emailStatusInlineDraft, refreshRow, row]);

  const accessStatusCurrent = accessStatus || 'not_applied';

  const startAccessStatusInlineEdit = useCallback(() => {
    setAccessStatusInlineDraft(accessStatusCurrent);
    setAccessStatusInlineEdit(true);
  }, [accessStatusCurrent]);

  const cancelAccessStatusInlineEdit = useCallback(() => {
    setAccessStatusInlineEdit(false);
    setAccessStatusInlineDraft(accessStatusCurrent);
  }, [accessStatusCurrent]);

  const commitAccessStatusInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    const next = accessStatusInlineDraft.trim();
    const prev = accessStatusCurrent.trim();
    if (!next || next === prev) {
      setAccessStatusInlineEdit(false);
      return;
    }
    if (!SUBSCRIBER_ACCESS_STATUSES.some((item) => item.value === next)) {
      toast.error('Invalid access status');
      return;
    }
    setAccessStatusSaving(true);
    try {
      await saveMcpAccessStatus(String(row.id), next);
      toast.success('Access status updated');
      setAccessStatus(next);
      setAccessStatusInlineEdit(false);
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
      toast.error(e instanceof Error ? e.message : 'Could not save access status');
    } finally {
      setAccessStatusSaving(false);
    }
  }, [accessStatusCurrent, accessStatusInlineDraft, row]);

  const channelSlugs = useMemo(
    () => channelsToKeyArray(row?.channels),
    [row],
  );

  const startChannelsInlineEdit = useCallback(() => {
    setChannelsInlineDraft(channelSlugs);
    setChannelsInlineEdit(true);
  }, [channelSlugs]);

  const cancelChannelsInlineEdit = useCallback(() => {
    setChannelsInlineEdit(false);
    setChannelsInlineDraft(channelSlugs);
  }, [channelSlugs]);

  const toggleChannelsInlineDraft = useCallback((slug: string) => {
    setChannelsInlineDraft((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }, []);

  const commitChannelsInlineEdit = useCallback(async () => {
    if (!row || row.id == null) return;
    if (channelSlugsEqual(channelsInlineDraft, channelSlugs)) {
      setChannelsInlineEdit(false);
      return;
    }
    setChannelsSaving(true);
    try {
      const channelsMap: Record<string, boolean> = {};
      for (const k of channelsInlineDraft) channelsMap[k] = true;
      const { error } = await supabase
        .from('subscribers')
        .update({
          channels: channelsMap,
          updated_at: new Date().toISOString(),
        })
        .eq('id', row.id as string | number);
      if (error) throw error;
      toast.success('Channels updated');
      setChannelsInlineEdit(false);
      await refreshRow();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
  toast.error(e instanceof Error ? e.message : 'Could not save channels');
    } finally {
      setChannelsSaving(false);
    }
  }, [channelSlugs, channelsInlineDraft, refreshRow, row]);

  useEffect(() => {
    void fetchActiveChannels()
      .then(setChannelCatalog)
      .catch(() => {});
  }, []);

  // Map of enrollment_id -> { id, name } for the linked automation, so the
  // scheduled emails table can render a small "via <automation>" badge.
  const automationByEnrollment = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    for (const e of enrollments) {
      if (e.automation) m.set(e.id, { id: e.automation.id, name: e.automation.name });
    }
    return m;
  }, [enrollments]);

  // Map of newsletter_id -> { id, name } for the linked newsletter, so the
  // scheduled emails table can render a small "via <newsletter>" badge for
  // rows materialized from the newsletter pipeline.
  const [newsletterById, setNewsletterById] = useState<Map<string, { id: string; name: string }>>(
    new Map(),
  );
  useEffect(() => {
    const ids = Array.from(
      new Set(
        scheduledEmails
          .map((r) => r.newsletter_id)
          .filter((v): v is string => typeof v === 'string' && v.length > 0),
      ),
    );
    if (ids.length === 0) {
      if (newsletterById.size > 0) setNewsletterById(new Map());
      return;
    }
    const missing = ids.filter((id) => !newsletterById.has(id));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('newsletters')
          .select('id, name')
          .in('id', missing);
        if (error) throw error;
        if (cancelled) return;
        setNewsletterById((prev) => {
          const next = new Map(prev);
          for (const n of (data ?? []) as Array<{ id: string; name: string | null }>) {
            next.set(n.id, { id: n.id, name: n.name ?? 'Newsletter' });
          }
          return next;
        });
      } catch (e) {
        console.warn('newsletter lookup failed:', e instanceof Error ? e.message : e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledEmails]);

  const extraEntries = useMemo(() => {
    if (!row) return [] as Array<[string, unknown]>;
    return Object.entries(row).filter(([k]) => !TOP_LEVEL_KEYS.has(k));
  }, [row]);

  const jsonString = useMemo(() => {
    if (!row) return '';
    try {
      return JSON.stringify(row, null, 2);
    } catch {
      return String(row);
    }
  }, [row]);

  const [sendingConfirm, setSendingConfirm] = useState(false);
  const emailStatusValue = subscriberEmailStatus(row).toLowerCase();
  const showInvitePanel = canSendNewsletterInvite(row);

  const refreshNewsletterInviteHistory = useCallback(async () => {
    if (!email.trim()) {
      setPostmarkInviteMessages([]);
      return;
    }
    try {
      const messages = await loadNewsletterInvitePostmarkMessages(loadPostmarkOutboundMessages, email);
      setPostmarkInviteMessages(messages);
    } catch {
      setPostmarkInviteMessages([]);
    }
  }, [email]);

  useEffect(() => {
    if (!showInvitePanel) {
      setPostmarkInviteMessages([]);
      return;
    }
    void refreshNewsletterInviteHistory();
  }, [showInvitePanel, refreshNewsletterInviteHistory, subscriberId]);

  // Deep link from MCP manual action: ?invite=1 opens the newsletter invite modal.
  useEffect(() => {
    if (loading || !row) return;
    if (searchParams.get('invite') !== '1') return;
    if (showInvitePanel) setInviteOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('invite');
        return next;
      },
      { replace: true },
    );
  }, [loading, row, showInvitePanel, searchParams, setSearchParams]);

  const newsletterInviteHistory = useMemo(
    () => mergeNewsletterInviteSendHistory(postmarkInviteMessages, scheduledEmails),
    [postmarkInviteMessages, scheduledEmails],
  );
  const lastNewsletterInvite = newsletterInviteHistory[0] ?? null;
  const needsConfirmation =
    !!email && emailStatusValue === 'pending';

  // While awaiting confirmation, poll the row so the panel and email status
  // update live the moment the subscriber clicks the confirm link.
  useEffect(() => {
    if (!needsConfirmation) return;
    const tick = () => void refreshRow();
    const interval = setInterval(tick, 5000);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', tick);
    };
  }, [needsConfirmation, refreshRow]);

  const prevEmailStatusRef = useRef(emailStatusValue);
  useEffect(() => {
    const prev = prevEmailStatusRef.current;
    prevEmailStatusRef.current = emailStatusValue;
    if (prev && prev !== 'confirmed' && emailStatusValue === 'confirmed') {
      toast.success('Email confirmed');
    }
  }, [emailStatusValue]);

  const handleSendConfirmation = useCallback(async () => {
    if (!email) return;
    setSendingConfirm(true);
    try {
      const { data, error } = await supabase.functions.invoke('llc-resend-confirm', {
        body: { email, rotate_token: rotateConfirmToken },
      });
      if (error) {
        const msg =
          (data as { error?: string } | null | undefined)?.error ||
          error.message ||
          'Could not send confirmation email';
        throw new Error(msg);
      }
      const rotated = (data as { token_rotated?: boolean } | null)?.token_rotated === true;
      toast.success(
        rotated
          ? `Confirmation email sent to ${email} (new link — older links won’t work)`
          : `Confirmation email sent to ${email}`,
      );
      setConfirmEmailOpen(false);
      void load();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
  toast.error(e instanceof Error ? e.message : 'Could not send confirmation email');
    } finally {
      setSendingConfirm(false);
    }
  }, [email, load, rotateConfirmToken]);

  const handleRecomputeProfile = useCallback(async () => {
    if (!subscriberId) return;
    setRecomputingProfile(true);
    try {
      const { error } = await supabase.rpc('recompute_subscriber_profile_score', {
        p_subscriber_id: subscriberId,
      });
      if (error) throw error;
      toast.success('Profile score updated');
      await load();
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
  toast.error(e instanceof Error ? e.message : 'Could not recompute profile score');
    } finally {
      setRecomputingProfile(false);
    }
  }, [subscriberId, load]);

  const handleDelete = async () => {
    if (!row) return;
    setDeleting(true);
    try {
      let deleteQuery = supabase.from('subscribers').delete();
      if (subscriberId) {
        deleteQuery = deleteQuery.eq('id', subscriberId);
      } else if (email) {
        deleteQuery = deleteQuery.eq('email', email);
      } else {
        toast.error('Cannot delete: missing id/email');
        return;
      }

      const { data, error } = await deleteQuery.select('id');
      if (error) throw error;
      if (!data?.length) {
        toast.message('Already removed', {
          description: 'This connection is no longer in the database.',
        });
        navigate('/subscribers');
        return;
      }

      setDeleteConfirmOpen(false);
      toast.success('Subscriber deleted');
      navigate('/subscribers');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Delete failed';
      toast.error(
        msg.includes('subscriber_not_found')
          ? 'Delete failed during profile sync. If this keeps happening, apply the latest database migration.'
          : msg,
      );
    } finally {
      setDeleting(false);
    }
  };

  const closeActionsMenu = () => setActionsMenuOpen(false);

  const handleReverseContactLookup = useCallback(async () => {
    if (!row || row.id == null) return;
    if (!email.trim()) {
      toast.error('No email on record');
      return;
    }
    setLookingUp(true);
    try {
      const result = await lookupSubscriberWithReverseContact(String(row.id));
      if (!result.found) {
        toast.message('No match', {
          description: 'Reverse Contact did not find this email. No credits used.',
        });
        return;
      }
      await refreshRow();
      setFieldsRefreshKey((key) => key + 1);
      const saved = result.updated.length ? `Updated ${result.updated.join(', ')}.` : 'Nothing new to save.';
      toast.success(result.name ? `Found ${result.name}` : 'Contact found', {
        description: result.photo_note ? `${saved} ${result.photo_note}` : saved,
      });
    } catch (e: unknown) {
      reportUiError('subscribers', 'operation', e);
      toast.error(e instanceof Error ? e.message : 'Lookup failed');
    } finally {
      setLookingUp(false);
    }
  }, [email, refreshRow, row]);

  const requestReverseContactLookup = useCallback(() => {
    if (!row || row.id == null) return;
    if (!email.trim()) {
      toast.error('No email on record');
      return;
    }
    if (lookingUp) return;
    if (hasReverseContactResult(customData)) {
      setLookupConfirmOpen(true);
      return;
    }
    void handleReverseContactLookup();
  }, [customData, email, handleReverseContactLookup, lookingUp, row]);

  const handleGmailFollowUp = useCallback(() => {
    if (!email.trim()) {
      toast.error('No email on record');
      return;
    }
    openConnectionFollowUpInGmail({
      email,
      greetingName: subscriberInviteGreetingName(row),
    });
  }, [email, row]);

  const actionsMenuTriggerClass =
    'inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';

  const renderActionsMenuPanel = (includeRefreshEdit: boolean) => (
    <div
      role="menu"
      className="absolute right-0 z-20 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1 shadow-lg"
    >
      {includeRefreshEdit ? (
        <>
          <button
            type="button"
            role="menuitem"
            disabled={loading}
            onClick={() => {
              closeActionsMenu();
              void load();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 shrink-0 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Refresh</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!row}
            onClick={() => {
              closeActionsMenu();
              setEditOpen(true);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Pencil className="w-4 h-4 shrink-0" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">Edit</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!email.trim() || lookingUp}
            onClick={() => {
              closeActionsMenu();
              void requestReverseContactLookup();
            }}
            className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            {lookingUp ? (
              <Loader2 className="w-4 h-4 shrink-0 mt-0.5 animate-spin text-gray-500" />
            ) : (
              <Search className="w-4 h-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
            )}
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900 dark:text-white">Lookup</span>
              <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
                Fill name, nickname, LinkedIn, and photo from this email.
              </span>
            </span>
          </button>
          <div className="my-1 border-t border-gray-200 dark:border-gray-700" role="separator" />
        </>
      ) : null}
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          closeActionsMenu();
          setEmailSummaryOpen(true);
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <Mail className="w-4 h-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Email summary</span>
          <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
            Sent and scheduled emails for this connection.
          </span>
        </span>
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!email.trim()}
        onClick={() => {
          closeActionsMenu();
          handleGmailFollowUp();
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
      >
        <SendHorizonal className="w-4 h-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Follow up in Gmail</span>
          <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
            Open Gmail compose with this contact and a follow-up draft.
          </span>
        </span>
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={recomputingProfile}
        onClick={() => {
          closeActionsMenu();
          void handleRecomputeProfile();
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
      >
        {recomputingProfile ? (
          <Loader2 className="w-4 h-4 shrink-0 mt-0.5 animate-spin text-gray-500" />
        ) : (
          <Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
        )}
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Score</span>
          <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
            Recompute AI profile richness for this connection.
          </span>
        </span>
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!email.trim()}
        onClick={() => {
          closeActionsMenu();
          setRecommendOpen(true);
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
      >
        <BookOpen className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Recommend content</span>
          <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
            Queue a site page email with one-click login links.
          </span>
        </span>
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!email.trim()}
        onClick={() => {
          closeActionsMenu();
          setTestLinkOpen(true);
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
      >
        <Link2 className="w-4 h-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Test content link</span>
          <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
            Copy a ?token= URL to test sign-in without sending email.
          </span>
        </span>
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={!row}
        onClick={() => {
          closeActionsMenu();
          setJsonOpen(true);
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none"
      >
        <Braces className="w-4 h-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Raw JSON</span>
          <span className="block text-[11px] leading-snug text-gray-500 dark:text-gray-400 mt-0.5">
            Full subscriber row from the database.
          </span>
        </span>
      </button>
      <div className="my-1 border-t border-gray-200 dark:border-gray-700" role="separator" />
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          closeActionsMenu();
          setDeleteConfirmOpen(true);
        }}
        className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-red-50 dark:hover:bg-red-950/30"
      >
        <Trash2 className="w-4 h-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-red-700 dark:text-red-300">Delete connection</span>
          <span className="block text-[11px] leading-snug text-red-600/80 dark:text-red-400/80 mt-0.5">
            Permanently remove this subscriber from the CRM.
          </span>
        </span>
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <Link
          to="/subscribers"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Connections
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,auto)_1fr_auto] gap-x-3 gap-y-2 md:gap-4 items-center mb-6">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center min-w-0 flex-1">
            <SubscriberHeaderImage customData={row?.custom_data} />
            <span className="truncate">{name || email || 'Subscriber'}</span>
          </h1>
          <div className="relative shrink-0 md:hidden" ref={mobileActionsMenuRef}>
            <button
              type="button"
              onClick={() => setActionsMenuOpen((open) => !open)}
              disabled={!row}
              aria-expanded={actionsMenuOpen}
              aria-haspopup="menu"
              aria-label="More actions"
              title="More actions"
              className={actionsMenuTriggerClass}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {actionsMenuOpen ? renderActionsMenuPanel(true) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300 min-w-0 md:justify-center">
          {email ? (
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-1.5 hover:text-emerald-600 dark:hover:text-emerald-400 truncate max-w-full"
            >
              <Mail className="w-4 h-4 shrink-0" />
              <span className="truncate">{email}</span>
            </a>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">No email on record</span>
          )}
          {row?.profile_score != null ? (
            <span className="inline-flex items-center gap-1 shrink-0">
              <span className="hidden sm:inline text-gray-400 dark:text-gray-500" aria-hidden="true">
                ·
              </span>
              <span>
                Profile score{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {row.profile_score}
                </span>
                {row.profile_score_tier ? (
                  <span className="text-gray-500 dark:text-gray-400">
                    {' '}
                    · {tierLabel(row.profile_score_tier)}
                  </span>
                ) : null}
              </span>
            </span>
          ) : null}
        </div>

        <div className="hidden md:flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => requestReverseContactLookup()}
            disabled={!email.trim() || lookingUp || !row}
            title="Look up this email in Reverse Contact"
            aria-label="Look up contact"
            className="inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="hidden xl:inline text-sm font-medium">Lookup</span>
          </button>
          <button
            type="button"
            onClick={handleGmailFollowUp}
            disabled={!email.trim()}
            title="Open Gmail with follow-up template"
            aria-label="Follow up in Gmail"
            className="inline-flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <SendHorizonal className="w-4 h-4" />
            <span className="hidden xl:inline text-sm font-medium">Follow up</span>
          </button>
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            disabled={!row}
            title="Edit subscriber"
            aria-label="Edit subscriber"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <div className="relative" ref={actionsMenuRef}>
            <button
              type="button"
              onClick={() => setActionsMenuOpen((open) => !open)}
              disabled={!row}
              aria-expanded={actionsMenuOpen}
              aria-haspopup="menu"
              aria-label="More actions"
              title="More actions"
              className={actionsMenuTriggerClass}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {actionsMenuOpen ? renderActionsMenuPanel(false) : null}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-12 flex justify-center">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      ) : loadError ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-8">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">Could not load subscriber.</p>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 break-words">{loadError}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
        </div>
      ) : notFound || !row ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-8 text-center">
          <p className="text-gray-700 dark:text-gray-200">Subscriber not found.</p>
          <Link
            to="/subscribers"
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to list
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          <TabBar
            activeTab={activeTab}
            onChange={setActiveTab}
            pendingScheduledCount={
              scheduledEmails.filter((r) => r.status === 'pending' || r.status === 'sending').length
            }
            automationsCount={enrollments.filter((e) => e.status === 'active').length}
            openTasksCount={openTasksCount}
          />

          {activeTab === 'details' ? (
            <>
            {showInvitePanel ? (
              <section
                className={`rounded-xl border p-5 ${
                  emailStatusValue === 'invited' || lastNewsletterInvite
                    ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-950/20'
                    : 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <Mail className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                        {emailStatusValue === 'invited' || lastNewsletterInvite
                          ? 'Invite sent — not subscribed yet'
                          : 'CRM contact — not on the newsletter yet'}
                      </p>
                      <p className="mt-0.5 text-sm text-emerald-800 dark:text-emerald-300">
                        Status is{' '}
                        <span className="font-medium">
                          {emailStatusValue === 'invited' ? 'invited' : 'raw'}
                        </span>
                        {emailStatusValue === 'invited'
                          ? ' — newsletter invite already sent.'
                          : ' — you added them manually.'}
                        {lastNewsletterInvite ? (
                          <>
                            {' '}
                            Last invite sent{' '}
                            <span className="font-medium">{formatWhen(lastNewsletterInvite.sentAt)}</span>
                            {newsletterInviteHistory.length > 1
                              ? ` (${newsletterInviteHistory.length} sends total)`
                              : ''}
                            {' '}
                            via Postmark (direct send, not scheduled). They can still subscribe at{' '}
                            <span className="font-medium">stephansmith.me/subscribe</span>.
                          </>
                        ) : (
                          <>
                            {' '}
                            Send a personal invite with your note; the email links to{' '}
                            <span className="font-medium">stephansmith.me/subscribe</span> so they can
                            pick topics and confirm.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInviteOpen(true)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium shrink-0 self-start sm:self-center ${
                      emailStatusValue === 'invited' || lastNewsletterInvite
                        ? 'border border-emerald-600 text-emerald-800 bg-white hover:bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-100 dark:border-emerald-500 dark:hover:bg-emerald-900/40'
                        : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    }`}
                  >
                    <Send className="w-4 h-4" />
                    {emailStatusValue === 'invited' || lastNewsletterInvite ? 'Send again' : 'Send invite'}
                  </button>
                </div>
              </section>
            ) : null}
            {needsConfirmation ? (
              <section className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        Email not confirmed
                      </p>
                      <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-300">
                        Status is{' '}
                        <span className="font-medium">{subscriberEmailStatus(row) || 'not set'}</span>.
                        Send a confirmation link to {email} — Postmark, or edit and send via Gmail.
                      </p>
                      <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-60"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-600"></span>
                        </span>
                        Live — this page updates automatically when they confirm
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row flex-wrap gap-2 shrink-0 self-start sm:self-center">
                    <button
                      type="button"
                      onClick={() => setConfirmEmailOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                    >
                      <Send className="w-4 h-4" />
                      Send via Postmark
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmGmailDraftOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-600 text-amber-900 bg-white hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-500 dark:hover:bg-amber-900/40 disabled:opacity-50 text-sm font-medium"
                      title="Edit the confirm draft, then open Gmail compose or send via Gmail SMTP"
                    >
                      <Mail className="w-4 h-4" />
                      Gmail
                    </button>
                  </div>
                </div>
              </section>
            ) : null}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              <div className="space-y-6 min-w-0">
              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    Details
                  </h2>
                </div>
                <dl className="divide-y divide-gray-200 dark:divide-gray-700">
                  <InlineTextDetailRow
                    label="Email"
                    displayText={email}
                    emptyPlaceholder="Add email"
                    editTitle="Click to edit email"
                    ariaLabel="Edit email"
                    inputType="email"
                    editing={emailInlineEdit}
                    draft={emailInlineDraft}
                    saving={emailSaving}
                    inputRef={emailInputRef}
                    onStartEdit={startEmailInlineEdit}
                    onDraftChange={setEmailInlineDraft}
                    onSave={() => void commitEmailInlineEdit()}
                    onCancel={cancelEmailInlineEdit}
                  />
                  <InlineTextDetailRow
                    label="Name"
                    displayText={name}
                    emptyPlaceholder="Add name"
                    editTitle="Click to edit name"
                    ariaLabel="Edit name"
                    editing={nameInlineEdit}
                    draft={nameInlineDraft}
                    saving={nameSaving}
                    inputRef={nameInputRef}
                    onStartEdit={startNameInlineEdit}
                    onDraftChange={setNameInlineDraft}
                    onSave={() => void commitNameInlineEdit()}
                    onCancel={cancelNameInlineEdit}
                  />
                  <InlineTextDetailRow
                    label="Role"
                    displayText={roleDisplay}
                    emptyPlaceholder="Add role"
                    editTitle={
                      role
                        ? `Click to edit role (${roleDisplay}${roleDisplay !== role ? ` · ${role}` : ''})`
                        : 'Click to edit role — e.g. founder, content creator'
                    }
                    ariaLabel="Edit role"
                    editing={roleInlineEdit}
                    draft={roleInlineDraft}
                    saving={roleSaving}
                    inputRef={roleInputRef}
                    onStartEdit={startRoleInlineEdit}
                    onDraftChange={setRoleInlineDraft}
                    onSave={() => void commitRoleInlineEdit()}
                    onCancel={cancelRoleInlineEdit}
                  />
                  <DetailRow
                    label="Profile score"
                    value={
                      row.profile_score != null ? (
                        <span title={JSON.stringify(row.profile_score_breakdown ?? {}, null, 0)}>
                          {row.profile_score}
                          {row.profile_score_tier
                            ? ` (${tierLabel(row.profile_score_tier)})`
                            : ''}
                        </span>
                      ) : (
                        '—'
                      )
                    }
                  />
                  <InlineSelectDetailRow
                    label="Email status"
                    displayText={
                      SUBSCRIBER_EMAIL_STATUSES.find((s) => s.value === emailStatusCurrent)?.label ||
                      emailStatusCurrent
                    }
                    emptyPlaceholder="Set email status"
                    editTitle="Click to edit email status"
                    ariaLabel="Edit email status"
                    editing={emailStatusInlineEdit}
                    draft={emailStatusInlineDraft}
                    saving={emailStatusSaving}
                    selectRef={emailStatusSelectRef}
                    options={SUBSCRIBER_EMAIL_STATUSES}
                    trailing={
                      emailStatusCurrent === 'unsubscribed' &&
                      typeof row.unsubscribed_at === 'string' &&
                      row.unsubscribed_at ? (
                        <span
                          className="text-xs text-gray-500 dark:text-gray-400 tabular-nums shrink-0 text-right"
                          title={formatWhen(row.unsubscribed_at)}
                        >
                          {formatUnsubscribeAgoLabel(row.unsubscribed_at)}
                        </span>
                      ) : null
                    }
                    onStartEdit={startEmailStatusInlineEdit}
                    onDraftChange={setEmailStatusInlineDraft}
                    onSave={() => void commitEmailStatusInlineEdit()}
                    onCancel={cancelEmailStatusInlineEdit}
                  />
                  <InlineSelectDetailRow
                    label="Access status"
                    displayText={
                      SUBSCRIBER_ACCESS_STATUSES.find((s) => s.value === accessStatusCurrent)
                        ?.label || accessStatusCurrent
                    }
                    emptyPlaceholder="Set access status"
                    editTitle="Click to edit product waitlist — waitlisted or invited"
                    ariaLabel="Edit access status"
                    editing={accessStatusInlineEdit}
                    draft={accessStatusInlineDraft}
                    saving={accessStatusSaving}
                    selectRef={accessStatusSelectRef}
                    options={SUBSCRIBER_ACCESS_STATUSES}
                    onStartEdit={startAccessStatusInlineEdit}
                    onDraftChange={setAccessStatusInlineDraft}
                    onSave={() => void commitAccessStatusInlineEdit()}
                    onCancel={cancelAccessStatusInlineEdit}
                  />
                  <DetailRow
                    label="Account UID"
                    mono={typeof row.user_id === 'string' && Boolean(row.user_id)}
                    value={
                      typeof row.user_id === 'string' && row.user_id ? (
                        <span className="break-all" title="Supabase auth user id">
                          {row.user_id}
                        </span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">Not provisioned</span>
                      )
                    }
                  />
                  <InlineTextDetailRow
                    label="Source"
                    displayText={sourceDisplay}
                    emptyPlaceholder="Add source"
                    editTitle={source ? `Click to edit source (${source})` : 'Click to edit source'}
                    ariaLabel="Edit source"
                    editing={sourceInlineEdit}
                    draft={sourceInlineDraft}
                    saving={sourceSaving}
                    inputRef={sourceInputRef}
                    onStartEdit={startSourceInlineEdit}
                    onDraftChange={setSourceInlineDraft}
                    onSave={() => void commitSourceInlineEdit()}
                    onCancel={cancelSourceInlineEdit}
                  />
                  <InlineChannelsDetailRow
                    channelSlugs={channelSlugs}
                    channelCatalog={channelCatalog}
                    editing={channelsInlineEdit}
                    draft={channelsInlineDraft}
                    saving={channelsSaving}
                    onStartEdit={startChannelsInlineEdit}
                    onToggleDraft={toggleChannelsInlineDraft}
                    onSave={() => void commitChannelsInlineEdit()}
                    onCancel={cancelChannelsInlineEdit}
                  />
                </dl>
              </section>

              {subscriberId ? (
                <SubscriberBookingsSection
                  subscriberId={subscriberId}
                  lastContactedAt={
                    typeof row.last_contacted_at === 'string' ? row.last_contacted_at : null
                  }
                  customData={customData}
                />
              ) : null}

              {subscriberId ? <SubscriberReferralsSection subscriberId={subscriberId} /> : null}

              {subscriberId ? (
                <SubscriberFunnelFieldsSection
                  subscriberId={subscriberId}
                  funnelStatus={typeof row.funnel_status === 'string' ? row.funnel_status : null}
                  lastContactedAt={
                    typeof row.last_contacted_at === 'string' ? row.last_contacted_at : null
                  }
                  onSaved={() => void load()}
                />
              ) : null}

              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                    Additional fields
                  </h2>
                </div>
                <dl className="divide-y divide-gray-200 dark:divide-gray-700">
                  {extraEntries.map(([k, v]) => (
                    <DetailRow
                      key={k}
                      label={k}
                      value={
                        v != null && typeof v === 'object' ? (
                          <pre className="text-xs font-mono whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-900/40 rounded-md p-2 max-h-48 overflow-auto">
                            {JSON.stringify(v, null, 2)}
                          </pre>
                        ) : (
                          renderScalar(v)
                        )
                      }
                    />
                  ))}
                  <DetailRow label="Created" value={formatWhen(row.created_at)} />
                  <DetailRow label="Updated" value={formatWhen(row.updated_at)} />
                </dl>
              </section>
              </div>

              {subscriberId ? (
                <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden lg:sticky lg:top-6">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                      Custom fields
                    </h2>
                  </div>
                  <SubscriberCustomFieldsForm
                    key={`${subscriberId}-${fieldsRefreshKey}`}
                    subscriberId={subscriberId}
                    customData={customData}
                    definitions={fieldDefinitions}
                    definitionsLoading={fieldDefinitionsLoading}
                    onFieldSaved={handleCustomFieldSaved}
                    compact
                  />
                </section>
              ) : null}
            </div>
            </>
          ) : null}

          {activeTab === 'tasks' ? (
            subscriberId ? (
              <SubscriberNetworkingTasksSection
                subscriberId={subscriberId}
                contactEmail={email}
                asTab
                onTasksChanged={handleTasksChanged}
              />
            ) : (
              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden p-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Save this connection before adding tasks.
                </p>
              </section>
            )
          ) : null}

          {activeTab === 'transcripts' ? (
            email.trim() ? (
              <SubscriberLinkedTranscriptsSection email={email} />
            ) : (
              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden p-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Add an email address to see linked transcripts.
                </p>
              </section>
            )
          ) : null}

          {activeTab === 'notes' ? (
            subscriberId ? (
              <RecordNotesPanel recordType="subscriber" recordId={subscriberId} />
            ) : (
              <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden p-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Save this connection before notes can be recorded.
                </p>
              </section>
            )
          ) : null}

          {activeTab === 'scheduled' ? (
            <ScheduledEmailsSection
              rows={scheduledEmails}
              loading={scheduledLoading}
              error={scheduledError}
              live={scheduledRealtime}
              automationByEnrollment={automationByEnrollment}
              newsletterById={newsletterById}
              onRefresh={() => void loadScheduledEmails()}
              onSchedule={() => setScheduleOpen(true)}
              onEdit={(r) => setEditingScheduled(r)}
            />
          ) : null}

          {activeTab === 'engagement' && subscriberId ? (
            <SubscriberEngagementSection subscriberId={subscriberId} />
          ) : null}

          {activeTab === 'site' && subscriberId ? (
            <SubscriberSiteActivitySection subscriberId={subscriberId} />
          ) : null}

          {activeTab === 'automations' ? (
            <SubscriberAutomationsSection
              rows={enrollments}
              loading={enrollmentsLoading}
              error={enrollmentsError}
              onRefresh={() => void loadEnrollments()}
              onAdd={() => setEnrollOpen(true)}
              onCancel={(eid) => void handleCancelEnrollment(eid)}
            />
          ) : null}

          {activeTab === 'postmark' ? (
            <SubscriberPostmarkSentSection email={email} />
          ) : null}

        </div>
      )}

      <SubscriberModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        subscriber={row}
        onSuccess={() => {
          void load();
        }}
      />

      {row ? (
        <>
          <SendSubscriberInviteModal
            isOpen={inviteOpen}
            onClose={() => setInviteOpen(false)}
            onSent={() => {
              void refreshNewsletterInviteHistory();
              void loadScheduledEmails();
              void refreshRow();
            }}
            toEmail={email}
            subscriberId={subscriberId}
            recipientName={subscriberInviteGreetingName(row) || null}
            previousSentAt={lastNewsletterInvite?.sentAt ?? null}
            previousSendCount={newsletterInviteHistory.length}
          />
          <EmailSummaryModal
            isOpen={emailSummaryOpen}
            onClose={() => setEmailSummaryOpen(false)}
            subscriber={row}
          />
        </>
      ) : null}

      <ScheduleEmailModal
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        subscriberId={subscriberId}
        subscriberEmail={email}
        onSuccess={() => {
          void loadScheduledEmails();
          setActiveTab('scheduled');
        }}
      />

      <RecommendContentModal
        isOpen={recommendOpen}
        onClose={() => setRecommendOpen(false)}
        subscriberId={subscriberId}
        subscriberEmail={email}
        subscriberName={name || null}
        onSuccess={() => {
          void loadScheduledEmails();
          setActiveTab('scheduled');
        }}
      />

      <TestContentLinkModal
        isOpen={testLinkOpen}
        onClose={() => setTestLinkOpen(false)}
        subscriberId={row?.id ?? subscriberId}
        subscriberEmail={email}
        subscriberName={name || null}
        emailStatus={subscriberEmailStatus(row)}
        manageToken={
          typeof row?.manage_token === 'string' ? row.manage_token : null
        }
        manageTokenExpiresAt={
          typeof row?.manage_token_expires_at === 'string'
            ? row.manage_token_expires_at
            : null
        }
        onTokenMinted={() => void refreshRow()}
      />

      <SubscriberJsonModal
        isOpen={jsonOpen}
        onClose={() => setJsonOpen(false)}
        jsonString={jsonString}
      />

      <ConfirmModal
        isOpen={lookupConfirmOpen}
        title="Look up again?"
        description="Reverse Contact already has a result on this connection. Running it again uses 2 credits."
        confirmLabel="Look up again"
        cancelLabel="Cancel"
        busy={lookingUp}
        onConfirm={() => {
          setLookupConfirmOpen(false);
          void handleReverseContactLookup();
        }}
        onClose={() => {
          if (!lookingUp) setLookupConfirmOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={confirmEmailOpen}
        title="Send confirmation via Postmark?"
        description={
          <div className="space-y-3">
            <p>
              Send a confirmation email to{' '}
              <span className="font-mono text-xs break-all">{email}</span>. Status becomes pending.
            </p>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={rotateConfirmToken}
                onChange={(e) => setRotateConfirmToken(e.target.checked)}
                disabled={sendingConfirm}
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
              />
              <span>
                Issue a <span className="font-medium">new</span> confirm link
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Leave unchecked to reuse the existing link so older emails still work.
                </span>
              </span>
            </label>
          </div>
        }
        confirmLabel="Send via Postmark"
        cancelLabel="Cancel"
        busy={sendingConfirm}
        onConfirm={() => void handleSendConfirmation()}
        onClose={() => {
          if (!sendingConfirm) setConfirmEmailOpen(false);
        }}
      />

      <ConfirmGmailDraftModal
        isOpen={confirmGmailDraftOpen}
        email={email}
        onClose={() => setConfirmGmailDraftOpen(false)}
        onDone={() => void load()}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        title="Delete connection?"
        description={
          <div className="space-y-2">
            <p>
              Permanently remove{' '}
              <span className="font-medium text-gray-900 dark:text-white">
                {name || email || 'this subscriber'}
              </span>
              {email && name ? (
                <>
                  {' '}
                  (
                  <span className="font-mono text-xs">{email}</span>)
                </>
              ) : null}{' '}
              from the CRM. This cannot be undone.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Scheduled emails, enrollments, and linked records for this connection may be
              affected depending on database rules.
            </p>
          </div>
        }
        confirmLabel="Delete connection"
        cancelLabel="Cancel"
        variant="danger"
        busy={deleting}
        onConfirm={() => void handleDelete()}
        onClose={() => {
          if (!deleting) setDeleteConfirmOpen(false);
        }}
      />

      <ScheduleEmailModal
        isOpen={!!editingScheduled}
        onClose={() => setEditingScheduled(null)}
        subscriberId={subscriberId}
        subscriberEmail={email}
        existing={editingScheduled}
        onSuccess={() => {
          void loadScheduledEmails();
        }}
      />

      <EnrollSubscriberModal
        isOpen={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        subscriberId={subscriberId}
        excludeAutomationIds={enrollments
          .filter((e) => e.status === 'active' && e.automation)
          .map((e) => e.automation!.id)}
        onSuccess={() => {
          setActiveTab('automations');
          void loadEnrollments();
          void loadScheduledEmails();
        }}
      />
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-2 sm:gap-4 px-6 py-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 self-center">
        {label}
      </dt>
      <dd
        className={`text-sm text-gray-900 dark:text-gray-100 break-words ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

function channelSlugsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((slug, i) => slug === right[i]);
}

function buildChannelsByVisibility(
  catalog: ChannelRow[],
  selectedSlugs: string[],
): Record<ChannelVisibility, ChannelRow[]> {
  const groups: Record<ChannelVisibility, ChannelRow[]> = {
    public: [],
    private: [],
    internal: [],
  };
  for (const c of catalog) {
    groups[c.visibility]?.push(c);
  }
  for (const slug of selectedSlugs) {
    if (!catalog.some((c) => c.slug === slug)) {
      groups.internal.push({
        id: slug,
        slug,
        label: channelLabel(slug),
        description: null,
        visibility: 'internal',
        sort_order: 999,
        active: true,
        created_at: '',
        updated_at: '',
      });
    }
  }
  return groups;
}

function InlineChannelsDetailRow({
  channelSlugs,
  channelCatalog,
  editing,
  draft,
  saving,
  onStartEdit,
  onToggleDraft,
  onSave,
  onCancel,
}: {
  channelSlugs: string[];
  channelCatalog: ChannelRow[];
  editing: boolean;
  draft: string[];
  saving: boolean;
  onStartEdit: () => void;
  onToggleDraft: (slug: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const channelsByVisibility = useMemo(
    () => buildChannelsByVisibility(channelCatalog, draft),
    [channelCatalog, draft],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-2 sm:gap-4 px-6 py-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 self-start pt-0.5">
        Channels
      </dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100 break-words min-w-0">
        {editing ? (
          <div
            className="space-y-3"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
              }
            }}
          >
            {(['public', 'private', 'internal'] as const).map((vis) => {
              const items = channelsByVisibility[vis];
              if (!items?.length) return null;
              return (
                <div key={vis}>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                    {visibilityLabel(vis)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {items.map((ch) => {
                      const active = draft.includes(ch.slug);
                      return (
                        <button
                          key={ch.slug}
                          type="button"
                          onClick={() => onToggleDraft(ch.slug)}
                          disabled={saving}
                          aria-pressed={active}
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 ${
                            active
                              ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                          title={ch.description ?? ch.slug}
                        >
                          {ch.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <XCircle className="w-3.5 h-3.5" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onStartEdit}
            className="group inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-md -mx-1 px-1 py-0.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            title="Click to edit channels"
          >
            {channelSlugs.length > 0 ? (
              channelSlugs.map((s) => (
                <span
                  key={s}
                  className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                >
                  {channelLabel(s, channelCatalog)}
                </span>
              ))
            ) : (
              <span className="italic text-gray-500 dark:text-gray-400">Add channels</span>
            )}
            <Pencil className="w-3.5 h-3.5 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
          </button>
        )}
      </dd>
    </div>
  );
}

function InlineTextDetailRow({
  label,
  displayText,
  emptyPlaceholder,
  editTitle,
  ariaLabel,
  inputType = 'text',
  editing,
  draft,
  saving,
  inputRef,
  onStartEdit,
  onDraftChange,
  onSave,
  onCancel,
}: {
  label: string;
  displayText: string;
  emptyPlaceholder: string;
  editTitle: string;
  ariaLabel: string;
  inputType?: 'text' | 'email';
  editing: boolean;
  draft: string;
  saving: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onStartEdit: () => void;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-2 sm:gap-4 px-6 py-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 self-center">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100 break-words min-w-0">
        {editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={inputRef}
              type={inputType}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSave();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancel();
                }
              }}
              className="flex-1 min-w-[12rem] px-2.5 py-1.5 text-sm border border-emerald-300 dark:border-emerald-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              aria-label={ariaLabel}
            />
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <XCircle className="w-3.5 h-3.5" />
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onStartEdit}
            className="group inline-flex max-w-full items-center gap-1.5 rounded-md -mx-1 px-1 py-0.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            title={editTitle}
          >
            <span className={displayText ? '' : 'italic text-gray-500 dark:text-gray-400'}>
              {displayText || emptyPlaceholder}
            </span>
            <Pencil className="w-3.5 h-3.5 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
          </button>
        )}
      </dd>
    </div>
  );
}

function InlineSelectDetailRow({
  label,
  displayText,
  emptyPlaceholder,
  editTitle,
  ariaLabel,
  editing,
  draft,
  saving,
  selectRef,
  options,
  trailing,
  onStartEdit,
  onDraftChange,
  onSave,
  onCancel,
}: {
  label: string;
  displayText: string;
  emptyPlaceholder: string;
  editTitle: string;
  ariaLabel: string;
  editing: boolean;
  draft: string;
  saving: boolean;
  selectRef: React.RefObject<HTMLSelectElement | null>;
  options: ReadonlyArray<{ value: string; label: string }>;
  trailing?: React.ReactNode;
  onStartEdit: () => void;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-2 sm:gap-4 px-6 py-3">
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 self-center">
        {label}
      </dt>
      <dd className="text-sm text-gray-900 dark:text-gray-100 break-words min-w-0">
        {editing ? (
          <div className="flex flex-wrap items-center gap-2">
            <select
              ref={selectRef}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              disabled={saving}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancel();
                }
              }}
              className="flex-1 min-w-[12rem] px-2.5 py-1.5 text-sm border border-emerald-300 dark:border-emerald-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              aria-label={ariaLabel}
            >
              {options.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
              {draft && !options.some((item) => item.value === draft) ? (
                <option value={draft}>{draft}</option>
              ) : null}
            </select>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <XCircle className="w-3.5 h-3.5" />
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 min-w-0">
            <button
              type="button"
              onClick={onStartEdit}
              className="group inline-flex max-w-full items-center gap-1.5 rounded-md -mx-1 px-1 py-0.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              title={editTitle}
            >
              {displayText ? (
                <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                  {displayText}
                </span>
              ) : (
                <span className="italic text-gray-500 dark:text-gray-400">{emptyPlaceholder}</span>
              )}
              <Pencil className="w-3.5 h-3.5 shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity" />
            </button>
            {trailing}
          </div>
        )}
      </dd>
    </div>
  );
}

const STATUS_META: Record<
  ScheduledEmailStatus,
  { label: string; icon: React.ComponentType<{ className?: string }>; classes: string }
> = {
  pending: {
    label: 'Pending',
    icon: Clock,
    classes:
      'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800',
  },
  sending: {
    label: 'Sending',
    icon: Loader2,
    classes:
      'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800',
  },
  sent: {
    label: 'Sent',
    icon: Send,
    classes:
      'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800',
  },
  failed: {
    label: 'Failed',
    icon: AlertTriangle,
    classes:
      'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800',
  },
  canceled: {
    label: 'Canceled',
    icon: XCircle,
    classes:
      'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  },
};

function StatusPill({ status }: { status: ScheduledEmailStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${meta.classes}`}
    >
      <Icon className={`w-3 h-3 ${status === 'sending' ? 'animate-spin' : ''}`} />
      {meta.label}
    </span>
  );
}

function TabBar({
  activeTab,
  onChange,
  pendingScheduledCount,
  automationsCount,
  openTasksCount,
}: {
  activeTab: DetailTab;
  onChange: (tab: DetailTab) => void;
  pendingScheduledCount: number;
  automationsCount: number;
  openTasksCount: number;
}) {
  const tabs: Array<{
    id: DetailTab;
    label: string;
    badge?: number | null;
    badgeStyle?: 'pill' | 'green-number';
  }> = [
    { id: 'details', label: 'Details' },
    {
      id: 'tasks',
      label: 'Tasks',
      badge: openTasksCount > 0 ? openTasksCount : null,
    },
    { id: 'transcripts', label: 'Transcripts' },
    { id: 'notes', label: 'Notes' },
    {
      id: 'scheduled',
      label: 'Scheduled Emails',
      badge: pendingScheduledCount > 0 ? pendingScheduledCount : null,
      badgeStyle: 'green-number',
    },
    { id: 'engagement', label: 'Engagement' },
    { id: 'site', label: 'Analytics' },
    {
      id: 'automations',
      label: 'Automations',
      badge: automationsCount > 0 ? automationsCount : null,
      badgeStyle: 'green-number',
    },
    { id: 'postmark', label: 'Postmark' },
  ];
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 overflow-hidden" role="tablist">
      <nav
        className="-mb-px flex gap-2 overflow-x-auto overflow-y-hidden"
        aria-label="Subscriber detail tabs"
      >
        {tabs.map((t) => {
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(t.id)}
              className={`whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                isActive
                  ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <span className="inline-flex items-center gap-2">
                {t.label}
                {t.badge != null ? (
                  t.badgeStyle === 'green-number' ? (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-semibold tabular-nums border border-emerald-200 dark:border-emerald-800 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                      {t.badge}
                    </span>
                  ) : (
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                        isActive
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {t.badge}
                    </span>
                  )
                ) : null}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

const ENROLLMENT_STATUS_META: Record<
  AutomationEnrollmentStatus,
  { label: string; icon: React.ComponentType<{ className?: string }>; classes: string }
> = {
  active: {
    label: 'Active',
    icon: Clock,
    classes:
      'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    classes:
      'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border-blue-200 dark:border-blue-800',
  },
  canceled: {
    label: 'Canceled',
    icon: XCircle,
    classes:
      'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  },
};

function SubscriberAutomationsSection({
  rows,
  loading,
  error,
  onRefresh,
  onAdd,
  onCancel,
}: {
  rows: EnrollmentWithAutomation[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onAdd: () => void;
  onCancel: (enrollmentId: string) => void;
}) {
  const activeCount = rows.filter((r) => r.status === 'active').length;

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Workflow className="w-4 h-4 text-emerald-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Automations
          </h2>
          {rows.length > 0 ? (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {activeCount} active · {rows.length} total
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="w-3.5 h-3.5" />
            Add to automation
          </button>
        </div>
      </div>

      {error ? (
        <div className="px-6 py-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : loading && rows.length === 0 ? (
        <div className="px-6 py-8 flex justify-center">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This subscriber isn&rsquo;t in any automations yet.
          </p>
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" />
            Add to automation
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Automation
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Enrolled
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((r) => {
                const meta = ENROLLMENT_STATUS_META[r.status];
                const Icon = meta.icon;
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-3 text-sm">
                      {r.automation ? (
                        <Link
                          to={`/automations/${r.automation.id}`}
                          className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
                        >
                          {r.automation.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">deleted automation</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${meta.classes}`}
                      >
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                      {formatWhen(r.enrolled_at)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.status === 'active' ? (
                        <button
                          type="button"
                          onClick={() => onCancel(r.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type ScheduledFilter = 'all' | 'adhoc' | string; // string = enrollment_id

function ScheduledEmailsSection({
  rows,
  loading,
  error,
  live,
  automationByEnrollment,
  newsletterById,
  onRefresh,
  onSchedule,
  onEdit,
}: {
  rows: ScheduledEmailRow[];
  loading: boolean;
  error: string | null;
  live: boolean;
  automationByEnrollment: Map<string, { id: string; name: string }>;
  newsletterById: Map<string, { id: string; name: string }>;
  onRefresh: () => void;
  onSchedule: () => void;
  onEdit: (row: ScheduledEmailRow) => void;
}) {
  const [filter, setFilter] = useState<ScheduledFilter>('all');
  const [sendNowRow, setSendNowRow] = useState<ScheduledEmailRow | null>(null);

  // Build filter options based on which enrollments actually have rows.
  // We dedupe by enrollment_id so each automation appears once, but the
  // option key remains the enrollment_id so the filter stays scoped to a
  // single fan-out (a subscriber could be enrolled in the same automation
  // twice in theory).
  const filterOptions = useMemo(() => {
    const enrollmentCounts = new Map<string, number>();
    let adhocCount = 0;
    for (const r of rows) {
      const eid = r.automation_enrollment_id;
      if (eid) enrollmentCounts.set(eid, (enrollmentCounts.get(eid) ?? 0) + 1);
      else adhocCount += 1;
    }
    const enrollmentOpts = Array.from(enrollmentCounts.entries())
      .map(([eid, count]) => {
        const automation = automationByEnrollment.get(eid);
        return {
          value: eid,
          label: automation?.name ?? 'Automation',
          count,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return { enrollmentOpts, adhocCount };
  }, [rows, automationByEnrollment]);

  const showFilter =
    filterOptions.enrollmentOpts.length +
      (filterOptions.adhocCount > 0 ? 1 : 0) >
    1;

  // Reset the filter if the selected source disappears (e.g. enrollment
  // cancelled and rows wiped).
  useEffect(() => {
    if (filter === 'all') return;
    if (filter === 'adhoc') {
      if (filterOptions.adhocCount === 0) setFilter('all');
      return;
    }
    if (!filterOptions.enrollmentOpts.some((o) => o.value === filter)) {
      setFilter('all');
    }
  }, [filter, filterOptions]);

  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'adhoc') return rows.filter((r) => !r.automation_enrollment_id);
    return rows.filter((r) => r.automation_enrollment_id === filter);
  }, [rows, filter]);

  const pendingCount = filteredRows.filter(
    (r) => r.status === 'pending' || r.status === 'sending',
  ).length;
  const sentCount = filteredRows.filter((r) => r.status === 'sent').length;
  const failedCount = filteredRows.filter((r) => r.status === 'failed').length;

  // Tick `now` every 15s so the "(sending in X)" countdown updates in place
  // without requiring a data refetch.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  const sendNowAutomationName = sendNowRow?.automation_enrollment_id
    ? automationByEnrollment.get(sendNowRow.automation_enrollment_id)?.name ?? null
    : null;

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <SendScheduledEmailNowModal
        isOpen={sendNowRow != null}
        row={sendNowRow}
        automationName={sendNowAutomationName}
        onClose={() => setSendNowRow(null)}
        onSuccess={() => {
          toast.success('Send queued — refreshing status…');
          onRefresh();
        }}
      />

      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarClock className="w-4 h-4 text-emerald-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Emails
          </h2>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wide border ${
              live
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700'
            }`}
            title={live ? 'Realtime updates active' : 'Polling for updates'}
          >
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full ${
                live ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
              }`}
              aria-hidden="true"
            />
            {live ? 'Live' : 'Polling'}
          </span>
          {rows.length > 0 ? (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {pendingCount} pending · {sentCount} sent
              {failedCount > 0 ? ` · ${failedCount} failed` : ''} ·{' '}
              {filter === 'all'
                ? `${rows.length} total`
                : `${filteredRows.length} of ${rows.length} total`}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showFilter ? (
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as ScheduledFilter)}
              className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              title="Filter by automation"
            >
              <option value="all">All sources ({rows.length})</option>
              {filterOptions.adhocCount > 0 ? (
                <option value="adhoc">
                  Ad-hoc only ({filterOptions.adhocCount})
                </option>
              ) : null}
              {filterOptions.enrollmentOpts.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} ({o.count})
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={onSchedule}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="w-3.5 h-3.5" />
            Schedule email
          </button>
        </div>
      </div>

      {error ? (
        <div className="px-6 py-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : loading && rows.length === 0 ? (
        <div className="px-6 py-8 flex justify-center">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No scheduled emails for this subscriber yet.
          </p>
          <button
            type="button"
            onClick={onSchedule}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" />
            Schedule an email
          </button>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No scheduled emails match the current filter.
          </p>
          <button
            type="button"
            onClick={() => setFilter('all')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Show all
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Scheduled
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Attempts
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => onEdit(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onEdit(r);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Edit ${r.subject || 'scheduled email'}`}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-900/30 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-[28rem]">
                    {(() => {
                      // Display priority for the primary label:
                      //   1. step/admin name (most descriptive for templated rows)
                      //   2. subject (inline content)
                      //   3. fallback
                      const primary = r.name?.trim() || r.subject?.trim() || '(no subject)';
                      const secondary =
                        r.name?.trim() && r.subject?.trim() ? r.subject : null;
                      return (
                        <>
                          <div className="font-medium truncate" title={primary}>
                            {primary}
                          </div>
                          {secondary ? (
                            <div
                              className="text-xs text-gray-500 dark:text-gray-400 truncate"
                              title={secondary}
                            >
                              {secondary}
                            </div>
                          ) : null}
                        </>
                      );
                    })()}
                    <div className="mt-1 flex items-center flex-wrap gap-1">
                      {r.postmark_template_alias ? (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-800"
                          title={`Postmark template: ${r.postmark_template_alias}`}
                        >
                          <FileCode className="w-3 h-3" />
                          {r.postmark_template_alias}
                        </span>
                      ) : null}
                      {(() => {
                        const eid = r.automation_enrollment_id;
                        if (!eid) return null;
                        const automation = automationByEnrollment.get(eid);
                        if (!automation) return null;
                        return (
                          <Link
                            to={`/automations/${automation.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
                            title={`Scheduled by automation: ${automation.name}`}
                          >
                            <Workflow className="w-3 h-3" />
                            via {automation.name}
                          </Link>
                        );
                      })()}
                      <PersonalizationStatusPill row={r} />
                      {(() => {
                        const nid = r.newsletter_id;
                        if (!nid) return null;
                        const newsletter = newsletterById.get(nid);
                        // Even if the lookup is still pending, link by id so
                        // the user can navigate. We just won't show the name.
                        const label = newsletter?.name ?? 'Newsletter';
                        return (
                          <Link
                            to={`/outgoing-newsletters/${nid}/content`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200 border border-sky-200 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900/50"
                            title={`Scheduled by newsletter: ${label}`}
                          >
                            <Mail className="w-3 h-3" />
                            via {label}
                          </Link>
                        );
                      })()}
                    </div>
                    {r.error && (r.status === 'failed' || r.status === 'sending') ? (
                      <div className="mt-1 text-xs text-red-700 dark:text-red-300 break-words">
                        {r.error}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                    <div>{formatWhen(r.scheduled_at)}</div>
                    {(() => {
                      if (r.status === 'pending') {
                        const hint = formatSendingIn(r.scheduled_at, nowMs);
                        return hint ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400">({hint})</div>
                        ) : null;
                      }
                      const ago = formatTimeAgo(r.scheduled_at, nowMs);
                      return ago ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          (scheduled {ago})
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                    {r.sent_at ? (
                      <>
                        <div>{formatWhen(r.sent_at)}</div>
                        {(() => {
                          const ago = formatTimeAgo(r.sent_at, nowMs);
                          return ago ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400">({ago})</div>
                          ) : null;
                        })()}
                      </>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    {r.status === 'pending' ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSendNowRow(r);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                        title="Send this email now (not on the scheduled date)"
                      >
                        <SendHorizonal className="w-3.5 h-3.5" />
                        Send now
                      </button>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                    {r.attempts > 0 ? (
                      <span className="text-gray-700 dark:text-gray-200">{r.attempts}</span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
